import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { styles } from '../styles';
import { SectionWrapper } from '../hoc';
import { slideIn, fadeIn, textVariant } from '../utils/motion';
import { getKey } from '../config/keys';

const Contact = () => {
	const formRef = useRef();
	const [form, setForm] = useState({ name: '', email: '', message: '' });
	const [loading, setLoading] = useState(false);

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setLoading(true);

		emailjs
			.send(
				getKey('EMAILJS_SERVICE_ID'),
				getKey('EMAILJS_TEMPLATE_ID'),
				{
					from_name: form.name,
					to_name: 'Sachin Saurav',
					from_email: form.email,
					to_email: 'hello@sachinsaurav.dev',
					message: form.message,
				},
				getKey('EMAILJS_PUBLIC_KEY')
			)
			.then(
				() => {
					setLoading(false);
					toast.success('Thank you! I will get back to you as soon as possible.');
					setForm({ name: '', email: '', message: '' });
				},
				(error) => {
					setLoading(false);
					console.error('Error:', error);
					toast.error('Something went wrong. Please try again.');
				}
			);
	};

	return (
		<>
			<motion.div variants={textVariant()}>
				<p className={styles.sectionSubText}>Get in touch</p>
				<h2 className={styles.sectionHeadText}>Contact</h2>
			</motion.div>

			<div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
				<motion.div
					variants={slideIn('left', 'tween', 0.1, 0.4)}
					className="lg:col-span-3">
					<form
						ref={formRef}
						onSubmit={handleSubmit}
						className="flex flex-col gap-6"
						autoComplete="off">
						<label className="flex flex-col gap-2">
							<span className="text-primary text-[14px] font-medium">Name</span>
							<input
								type="text"
								name="name"
								value={form.name}
								onChange={handleChange}
								placeholder="Your name"
								required
								className="px-4 py-3 bg-surface border border-border rounded-lg text-primary text-[15px] placeholder:text-muted/50 outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-colors"
							/>
						</label>
						<label className="flex flex-col gap-2">
							<span className="text-primary text-[14px] font-medium">Email</span>
							<input
								type="email"
								name="email"
								value={form.email}
								onChange={handleChange}
								placeholder="you@company.com"
								required
								className="px-4 py-3 bg-surface border border-border rounded-lg text-primary text-[15px] placeholder:text-muted/50 outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-colors"
							/>
						</label>
						<label className="flex flex-col gap-2">
							<span className="text-primary text-[14px] font-medium">Message</span>
							<textarea
								rows={5}
								name="message"
								value={form.message}
								onChange={handleChange}
								placeholder="Tell me about your project..."
								required
								className="px-4 py-3 bg-surface border border-border rounded-lg text-primary text-[15px] placeholder:text-muted/50 outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-colors resize-none"
							/>
						</label>
						<button
							type="submit"
							disabled={loading}
							className="bg-accent text-white py-3 px-8 rounded-lg font-medium text-[15px] w-fit hover:bg-accent-dark disabled:opacity-50 transition-colors duration-200">
							{loading ? 'Sending...' : 'Send Message'}
						</button>
					</form>
				</motion.div>

				<motion.div
					variants={fadeIn('', '', 0.2, 0.4)}
					className="lg:col-span-2 flex flex-col gap-8">
					<div>
						<h4 className="text-primary text-[16px] font-semibold mb-4">Other ways to reach me</h4>
						<div className="space-y-4">
							<a
								href="mailto:hello@sachinsaurav.dev"
								className="flex items-center gap-3 text-muted hover:text-accent transition-colors group">
								<div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center group-hover:border-accent/30 transition-colors">
									<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
									</svg>
								</div>
								<div>
									<p className="text-[14px] font-medium">Email</p>
									<p className="text-[13px]">hello@sachinsaurav.dev</p>
								</div>
							</a>
							<a
								href="tel:+917677355469"
								className="flex items-center gap-3 text-muted hover:text-accent transition-colors group">
								<div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center group-hover:border-accent/30 transition-colors">
									<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
									</svg>
								</div>
								<div>
									<p className="text-[14px] font-medium">Phone</p>
									<p className="text-[13px]">+91 7677355469</p>
								</div>
							</a>
							<a
								href="https://in.linkedin.com/in/ersachinsaurav"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-3 text-muted hover:text-accent transition-colors group">
								<div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center group-hover:border-accent/30 transition-colors">
									<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
										<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
									</svg>
								</div>
								<div>
									<p className="text-[14px] font-medium">LinkedIn</p>
									<p className="text-[13px]">Connect with me</p>
								</div>
							</a>
							<a
								href="https://github.com/ersachinsaurav"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-3 text-muted hover:text-accent transition-colors group">
								<div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center group-hover:border-accent/30 transition-colors">
									<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
										<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
									</svg>
								</div>
								<div>
									<p className="text-[14px] font-medium">GitHub</p>
									<p className="text-[13px]">View my work</p>
								</div>
							</a>
						</div>
					</div>
				</motion.div>
			</div>
		</>
	);
};

export default SectionWrapper(Contact, 'contact');
